import { OrderDto, UrlKeyDto } from '@trucktrax/trucktrax-ts-common';
import findKey from 'lodash/findKey';
import isEqual from 'lodash/isEqual';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { SEARCH_VEHICLE_AND_ORDER_HINT } from '../../../constants/appConstants';
import { MARKER_TYPE_ORDER, MARKER_TYPE_TRUCK } from '../../../constants/mapConstants';
import { selectTruckMarker } from '../../../services/ticketsService';
import {
  closeAllPopups,
  MarkerSelectedFrom,
  openPopup,
  selectMarker,
} from '../../../store/actions/mapviewActions';
import { getValidOrdersListInDateRange } from '../../../util/orderUtil';
import Autocomplete from '../forms/Autocomplete';
import styles from './VehicleSearch.module.css';
import { ReduxState } from '../../../store';
import { activeMarkersSelector } from '../../../store/selectors/markerSelectors';
import { MapMarker, MapMarkerType } from '../../../types';

export interface VehicleAndOrderSearchState {
  dataSource: any[];
  searchText: string;
  hintText: string;
  errorText?: string;
  dataSourceConfig: {
    text: string;
    value: string;
    label: string;
    icon: string;
  };
}

export class VehicleAndOrderSearch extends Component<
  VehicleAndOrderSearchProps,
  VehicleAndOrderSearchState
> {
  static defaultProps: Partial<VehicleAndOrderSearchProps> = {
    currentRegion: undefined,
    ordersList: [],
  };

  constructor(props: VehicleAndOrderSearchProps) {
    super(props);

    this.state = {
      dataSource: [],
      searchText: '',
      hintText: SEARCH_VEHICLE_AND_ORDER_HINT,
      errorText: '',
      dataSourceConfig: {
        text: 'objectId',
        value: 'markerId',
        label: 'type',
        icon: 'icon',
      },
    };
  }

  componentDidUpdate(prevProps: VehicleAndOrderSearchProps) {
    const {
      markers: prevMarkers,
      currentRegion: prevCurrentRegion,
      ordersList: prevOrderList,
    } = prevProps;
    const { markers, currentRegion, ordersList } = this.props;
    const hasDataSourceRequiredData = !!(
      this.props.markers &&
      this.props.currentRegion &&
      this.props.ordersList
    );
    const canUpdate =
      !isEqual(prevMarkers, markers) ||
      !isEqual(prevCurrentRegion, currentRegion) ||
      !isEqual(prevOrderList, ordersList);

    if (canUpdate && hasDataSourceRequiredData) {
      const truckData = markers.map(marker => ({
        objectId: marker.name,
        icon: 'icon-truck-dump',
        ...marker.data,
        type: marker.type,
        markerData: null,
        isExternal: marker.data.isExternal,
      }));

      const validOrdersList = Array.isArray(ordersList) ? ordersList : [];
      const orderData = getValidOrdersListInDateRange(validOrdersList).map((order: OrderDto) => ({
        isActive: true,
        status: null,
        ticketUrl: '',
        objectId: order.orderNumber.toString(),
        markerId: order.id.toString(),
        icon: 'icon-orders',
        type: MARKER_TYPE_ORDER as MapMarkerType,
        lat: order.geoZone.zone.circle.center.latitude,
        lng: order.geoZone.zone.circle.center.longitude,
        markerData: order,
        isExternal: true,
      }));

      const dataSource = truckData.concat(orderData);

      this.setState({
        dataSource,
        errorText: undefined,
      });
    }
  }

  handleUpdateInput = (searchText: string) => {
    this.setState({
      searchText,
      errorText: undefined,
    });
  };

  handleNewRequest = (value: any, reset: () => void) => {
    // try to find the value based on the search text
    const key = findKey(this.state.dataSource, o => o.objectId === value)!;
    const markerByTextSearch = this.state.dataSource[key as any];
    const selectedMarker = markerByTextSearch || value;
    this.props.closeAllPopups();

    this.setState(
      {
        hintText: SEARCH_VEHICLE_AND_ORDER_HINT,
      },
      () => {
        if (!selectedMarker) {
          this.setState({
            errorText: 'No results found.',
            searchText: '',
          });
          reset();
          return;
        }

        if (selectedMarker.type === MARKER_TYPE_TRUCK) {
          const ticketUrl = selectedMarker?.ticketUrl ?? '';
          this.props.selectTruckMarker(
            {
              ...selectedMarker,
              selectedFrom: MarkerSelectedFrom.VehicleAndOrderSearch,
            },
            ticketUrl,
          );
          this.setState({
            errorText: undefined,
          });
        } else {
          this.props.selectMarker(selectedMarker);
          this.setState({
            errorText: undefined,
          });
        }
      },
    );
  };

  clearSearch = () => {
    this.setState({ searchText: '' });
  };

  render() {
    return (
      <div className={styles.searchWrapper}>
        <Autocomplete
          dataTest="header-truck-and-order-search"
          hintText={this.state.hintText}
          onUpdateInput={this.handleUpdateInput}
          onNewRequest={this.handleNewRequest}
          searchText={this.state.searchText}
          dataSource={this.state.dataSource}
          dataSourceConfig={this.state.dataSourceConfig}
          errorText={this.state.errorText}
          openOnFocus={false}
          autocompleteHeight="500px"
          clearSearch={this.clearSearch}
          itemToString={(item: any) => (item === null ? '' : item.objectId)}
        />
      </div>
    );
  }
}

export type VehicleAndOrderSearchReduxStateProps = {
  markers: MapMarker[];
};

export type VehicleAndOrderSearchProps = VehicleAndOrderSearchReduxStateProps & {
  currentRegion?: UrlKeyDto;
  selectMarker: (marker: any) => void;
  closeAllPopups: () => void;
  ordersList?: OrderDto[];
  selectTruckMarker: (marker: any, ticketUrl: string) => Promise<void>;
};

function mapStateToProps(state: ReduxState, ownProps: Partial<VehicleAndOrderSearchProps>) {
  return {
    currentRegion: state.currentRegion,
    positions: state.positionList,
    selectedMarker: state.selectedMarker,
    markers: ownProps.markers ?? activeMarkersSelector(state),
  };
}

export default connect(mapStateToProps, {
  selectMarker,
  closeAllPopups,
  openPopup,
  selectTruckMarker,
})(VehicleAndOrderSearch);
